import { AlignmentApiService } from '@data-access/geospatial/alignments';
import {
  SectionMaterialConfig,
  TrimBimCapabilityProvider,
  TrajectoryMetadataProvider,
  SectionTag,
  TrajectoryMetadata,
} from '@trimble/web3d-providers';
import { MissionPathItem, TrajectoryItem } from '../data-state/DataStateModels';
import { AlignmentSettings, FormattingSettings } from '@trimble/types';
import { Color } from '@technology/web3d';
import { StartNavigationOptions } from '@technology/web3d-plugin-trimbim';

const LOADED_PATH_SECTION_LINE_WIDTH = 4;

function isSplittableMissionPath(
  item: TrajectoryItem
): item is MissionPathItem {
  return (
    (item as MissionPathItem).mission !== undefined &&
    item.isSplittable === true
  );
}

export async function loadTrajectory(
  alignmentApi: AlignmentApiService,
  tenantTrn: string,
  item: TrajectoryItem,
  settings: FormattingSettings,
  trimBimProvider: TrimBimCapabilityProvider,
  trajectoryMetadataProvider: TrajectoryMetadataProvider,
  /**
   * All per-pane trajectory metadata providers.  On reload, every pane's
   * provider is asked to prepare and save navigation state — the actively
   * navigating pane may be any of them, not just the primary
   * `trajectoryMetadataProvider` used for analysis and visual restore.
   */
  allTrajectoryMetadataProviders: readonly TrajectoryMetadataProvider[],
  /**
   * If true, the trajectory will be unloaded before loading it again.
   * If false and the trajectory is already loaded it will not be reloaded.
   */
  reloadModel: boolean,
  signal: AbortSignal,
  alignmentSettings: AlignmentSettings,
  resolveSplittable: (
    id: string,
    signal?: AbortSignal
  ) => Promise<TrajectoryItem | undefined>
): Promise<void> {
  signal.throwIfAborted();

  const resolvedItem = await resolveSplittable(item.id, signal);
  signal.throwIfAborted();
  if (!resolvedItem) {
    // Trajectory was removed from state before it could load; treat as a
    // cancellation so the framework doesn't record a phantom "loaded" model.
    throw new DOMException('Trajectory removed before load', 'AbortError');
  }
  item = resolvedItem;

  const useSections = isSplittableMissionPath(item);

  const sectionTags: SectionTag[] = useSections
    ? getMissionPathSectionTags(item as MissionPathItem)
    : [
        {
          tag: 'unscanned',
          beginStation: item.beginStation,
          endStation: item.endStation,
        },
      ];

  const trimBimData = await alignmentApi.exportMissionPathTrimbim(
    tenantTrn,
    item.id,
    sectionTags,
    settings.lengthUnit,
    settings.lengthDecimals ?? 0,
    signal,
    alignmentSettings.labelInterval,
    useSections ? 'Black' : undefined
  );

  signal.throwIfAborted();

  if (!trimBimData || trimBimData.byteLength === 0) {
    throw new Error(`Trajectory TrimBIM data is empty for item: ${item.id}`);
  }

  // Generate section materials dynamically only for splittable mission paths
  const sectionMaterials = useSections
    ? generateSectionMaterials(item as MissionPathItem)
    : undefined;

  const metadata: TrajectoryMetadata = {
    modelId: item.id,
    isAlignment: true,
    sectionTags,
    sectionMaterials,
    beginStation: item.beginStation,
    endStation: item.endStation,
    beginDistance: item.beginStation,
    endDistance: item.endStation,
  };

  let navState: StartNavigationOptions | undefined;
  if (reloadModel) {
    for (const provider of allTrajectoryMetadataProviders) {
      provider.prepareForReload(item.id, {
        invalidateMaterials: provider === trajectoryMetadataProvider,
      });
    }
    const navStates = await Promise.all(
      allTrajectoryMetadataProviders.map((provider) =>
        provider.saveNavigationState(item.id)
      )
    );
    navState = navStates.find((s) => s !== undefined);
  }

  // Analyze trajectory metadata first, then load the model
  await trajectoryMetadataProvider.analyzeAndStore(
    item.id,
    metadata,
    trimBimData
  );

  const bufferToLoad =
    trajectoryMetadataProvider.getTransformedBuffer(
      item.id,
      alignmentSettings,
      settings.lengthDecimals ?? 0
    ) ?? trimBimData;

  const model = await trimBimProvider.loadModel(
    item.id,
    bufferToLoad,
    false,
    reloadModel
  );

  if (model) {
    await trajectoryMetadataProvider.applyVisualsAndRestoreNavigation(
      item.id,
      navState
    );
  }
}

/**
 * Generates section material configurations for a mission path based on its runs.
 * Uses colors that were pre-generated and stored in the mission's runs.
 * @param missionPathItem The trajectory item with mission information
 * @returns Array of section material configurations
 */
export function generateSectionMaterials(
  missionPathItem: MissionPathItem
): SectionMaterialConfig[] {
  const { mission } = missionPathItem;

  if (!mission.runs || mission.runs.length === 0) {
    return [];
  }

  return mission.runs.map((run) => {
    const color = run.color
      ? new Color(run.color.r, run.color.g, run.color.b)
      : new Color(Color.NAMES.gray);
    return {
      sectionName: run.name,
      material: {
        lineWidth: LOADED_PATH_SECTION_LINE_WIDTH,
        color,
      },
      defaultColor: color.clone(),
      beginStation: run.beginStation!,
      endStation: run.endStation!,
    };
  });
}

function getMissionPathSectionTags(
  missionPathItem: MissionPathItem
): Array<SectionTag> {
  const {
    mission: { runs },
    beginStation,
    endStation,
  } = missionPathItem;
  const sectionTags =
    runs?.length &&
    typeof beginStation === 'number' &&
    typeof endStation === 'number'
      ? fillAndMergeSections(
          [...runs]
            .sort((a, b) => a.beginStation! - b.beginStation!)
            .map((run) => ({
              beginStation: run.beginStation,
              endStation: run.endStation!,
              tag: run.name,
            })),
          beginStation,
          endStation
        )
      : null;
  return sectionTags || [];
}

function fillAndMergeSections(
  sections: Array<SectionTag>,
  globalBegin: number,
  globalEnd: number
): Array<SectionTag> {
  if (globalBegin > globalEnd) {
    throw new Error('globalBegin must be <= globalEnd');
  }

  // Sort by beginStation
  const sorted = sections
    .filter((s) => s.endStation >= globalBegin && s.beginStation <= globalEnd)
    .sort((a, b) => a.beginStation - b.beginStation);

  const result: SectionTag[] = [];
  let current = globalBegin;

  for (const sec of sorted) {
    // Skip invalid or fully before/after sections
    if (sec.endStation < current) continue;

    // Fill gap before section
    if (sec.beginStation > current) {
      result.push({
        tag: 'unscanned',
        beginStation: current,
        endStation: sec.beginStation,
      });
    }

    // Add current section (trim to global range)
    const begin = Math.max(sec.beginStation, current);
    const end = Math.min(sec.endStation, globalEnd);
    result.push({ tag: sec.tag, beginStation: begin, endStation: end });

    current = end;
    if (current >= globalEnd) break;
  }

  // Fill remaining gap after last section
  if (current < globalEnd) {
    result.push({
      tag: 'unscanned',
      beginStation: current,
      endStation: globalEnd,
    });
  }

  // Merge adjacent sections with same tag
  const merged: SectionTag[] = [];
  for (const s of result) {
    const last = merged[merged.length - 1];
    if (last && last.tag === s.tag && last.endStation === s.beginStation) {
      last.endStation = s.endStation;
    } else {
      merged.push({ ...s });
    }
  }

  return merged;
}
